import * as React from "react";

import { useMemo, useState } from "react";

import Alert from "@mui/material/Alert";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";

import MarkdownContent from "./MarkdownContent";
import {
  getHelpManifest,
  getHelpSectionsForRoute,
  searchHelpSections,
} from "../../content/help";

function SectionAccordion({ section, defaultExpanded = false }) {
  return (
    <Accordion defaultExpanded={defaultExpanded}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack spacing={1} sx={{ width: "100%" }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {section.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {section.summary}
          </Typography>
          {Array.isArray(section.routes) && section.routes.length > 0 ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {section.routes.map((route) => (
                <Chip
                  key={`${section.id}-${route}`}
                  size="small"
                  label={route}
                  variant="outlined"
                />
              ))}
            </Stack>
          ) : null}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <MarkdownContent markdown={section.contentMarkdown} />
      </AccordionDetails>
    </Accordion>
  );
}

export default function ManualContent({
  route = "",
  introText = "Browse the generated manual content below. As the Word document is tagged more precisely, this view automatically reflects the latest generated sections.",
}) {
  const [query, setQuery] = useState("");

  const manifest = getHelpManifest();

  const contextualSections = useMemo(
    () => (route ? getHelpSectionsForRoute(route) : []),
    [route]
  );

  const searchResults = useMemo(() => searchHelpSections(query), [query]);

  const contextualIds = useMemo(
    () => new Set(contextualSections.map((section) => section.id)),
    [contextualSections]
  );

  const librarySections = useMemo(
    () =>
      searchResults.filter((section) => {
        if (!route) {
          return true;
        }

        return !contextualIds.has(section.id);
      }),
    [contextualIds, route, searchResults]
  );

  return (
    <Stack spacing={2} sx={{ pb: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {introText}
      </Typography>

      {manifest?.source?.commentCount ? (
        <Alert severity="info">
          The current manual draft still contains {manifest.source.commentCount}{" "}
          Word comments, so some content is still editorial draft material.
        </Alert>
      ) : null}

      {route ? (
        contextualSections.length > 0 ? (
          <Alert severity="success">
            Showing route-aware manual matches for <strong>{route}</strong>.
          </Alert>
        ) : (
          <Alert severity="warning">
            No manual sections are currently mapped to <strong>{route}</strong>.
            Add a `[meta:routes=...]` line under the relevant `[help:...]`
            heading to surface page-aware manual content here.
          </Alert>
        )
      ) : null}

      <TextField
        fullWidth
        label="Search manual"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search by title, keyword, or body text"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "text.secondary" }} />
            </InputAdornment>
          ),
        }}
      />

      {contextualSections.length > 0 ? (
        <Box>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Contextual Manual
          </Typography>
          <Stack spacing={1.5}>
            {contextualSections.map((section) => (
              <SectionAccordion
                key={section.id}
                section={section}
                defaultExpanded
              />
            ))}
          </Stack>
        </Box>
      ) : null}

      <Box>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Manual Library
        </Typography>
        {librarySections.length === 0 ? (
          <Alert severity="info">No manual sections match the current search.</Alert>
        ) : (
          <Stack spacing={1.5}>
            {librarySections.map((section, index) => (
              <SectionAccordion
                key={section.id}
                section={section}
                defaultExpanded={!route && !query && index === 0}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
